import React, {useEffect, useState} from "react";
import "./topbar.css";
import {
    Logout,
    PersonOutline,
    Search as SearchIcon,
    DarkMode as DarkModeIcon,
    WbSunny as LightModeIcon,
    Dashboard as DashboardIcon,
} from "@mui/icons-material";
import {
    Avatar,
    Menu,
    MenuItem,
    useMediaQuery,
    Tooltip,
    IconButton,
    Box,
    Divider,
    ListItemIcon,
    Fade,
    InputBase,
    Paper,
    Typography,
    useTheme,
    Switch,
    Skeleton,
    Button
} from "@mui/material";
import {Link, useLocation} from "react-router-dom";
import CryptoJS from "crypto-js";
import {KEY} from "../../dummyData";

export default function Topbar(props) {
    // Get current location
    const location = useLocation();

    // Check if current path is root
    const isRootPath = location.pathname === "/";

    // State variables
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [notificationsNumber, setNotificationsNumber] = useState(6);
    const [initialName, setInitialName] = useState("New User");
    const [name, setName] = useState(initialName);
    const [showNotifications, setShowNotifications] = useState(false);
    const [loading, setLoading] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const [propertyName, setPropertyName] = useState("");
    const [unitNumber, setUnitNumber] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showSearch, setShowSearch] = useState(false);

    // Media queries for responsive design
    const isMobile = useMediaQuery("(max-width:700px)");
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const theme = useTheme();

    // Menu states
    const menuOpen = Boolean(anchorEl);
    const notificationsOpen = Boolean(notificationsAnchorEl);

    // Get user data from localStorage
    useEffect(() => {
        setLoading(true);
        try {
            let originalText;
            if (localStorage.getItem("tenant_details") === null) {
                let dummyData = {
                    first_name: "",
                    last_name: "",
                };
                originalText = JSON.stringify(dummyData);
            } else {
                let details = CryptoJS.AES.decrypt(
                    localStorage.getItem("tenant_details"),
                    KEY
                );
                originalText = details.toString(CryptoJS.enc.Utf8);
            }

            let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));
            const firstName = tenantDetails.get("first_name") || "";
            const lastName = tenantDetails.get("last_name") || "";
            const property = tenantDetails.get("property_name") || "";
            const unit = tenantDetails.get("house_number") || "";

            setInitialName(firstName + " " + lastName);
            setPropertyName(property);
            setUnitNumber(unit);

            if (isMobile) {
                setName(firstName);
            } else {
                setName(firstName + " " + lastName);
            }

            // Simulate loading
            setTimeout(() => {
                setLoading(false);
            }, 800);
        } catch (e) {
            console.error("Error parsing user data:", e);
            setLoading(false);
        }
    }, [isMobile]);

    // Dark mode toggle
    const handleDarkModeToggle = () => {
        setDarkMode(!darkMode);
        // You would implement actual theme switching logic here
    };

    // Handle profile menu
    const handleProfileClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle notifications menu
    const handleNotificationsClick = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
        setShowNotifications(true);
    };

    // Close menus
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationsClose = () => {
        setNotificationsAnchorEl(null);
        setShowNotifications(false);
    };

    // Handle logout
    const logout = () => {
        localStorage.clear();
        props.onLogout && props.onLogout();
    };

    // Handle search
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        // Implement search functionality
        console.log("Searching for:", searchQuery);
        setSearchQuery("");
        setShowSearch(false);
    };

    const toggleSearch = () => {
        setShowSearch(!showSearch);
        if (!showSearch) {
            setTimeout(() => {
                document.getElementById("search-input")?.focus();
            }, 100);
        }
    };

    // Mark all notifications as read
    const markAllAsRead = () => {
        setNotificationsNumber(0);
        handleNotificationsClose();
    };

    // Get initials for avatar
    const getInitials = (string) => {
        if (!string || string.trim() === "") return "NU";

        let names = string.split(" "),
            initials = names[0].substring(0, 1).toUpperCase();

        if (names.length > 1) {
            initials += names[names.length - 1].substring(0, 1).toUpperCase();
        }
        return initials;
    };

    // Don't render component if we're on root path
    if (isRootPath) {
        return null;
    }

    return (
        <Paper
            elevation={1}
            sx={{
                borderRadius: 0,
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                backgroundColor: darkMode ? '#1E293B' : 'white',
                borderBottom: '1px solid',
                borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                color: darkMode ? 'white' : 'inherit',
                transition: 'all 0.3s ease'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: {xs: '8px 16px', md: '8px 24px'},
                    height: {xs: '64px', md: '70px'}
                }}
            >
                {/* Left section - REMOVED MENU BUTTON */}
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            ml: {xs: 0, sm: 1}
                        }}
                    >
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={120} height={24}/>
                                <Skeleton variant="text" width={160} height={16}/>
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: {xs: '1rem', md: '1.1rem'},
                                        color: darkMode ? 'white' : '#334155',
                                        lineHeight: 1.2
                                    }}
                                >
                                    Welcome, {name}
                                </Typography>
                                {(propertyName || unitNumber) && !isSmallScreen && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {propertyName}{unitNumber ? ` • Unit ${unitNumber}` : ''}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>


                {/* Right section */}
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    {/*  Search icon for mobile
                    {isMobile && !showSearch && (
                        <IconButton
                            onClick={toggleSearch}
                            sx={{
                                color: darkMode ? 'white' : 'inherit',
                                mr: 0.5
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    )}*/}

                    {/* Dark mode toggle */}
                    {!isMobile && (
                        <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"}>
                            <IconButton
                                onClick={handleDarkModeToggle}
                                sx={{
                                    mx: 0.5,
                                    color: darkMode ? 'white' : 'inherit'
                                }}
                            >
                                {darkMode ? <LightModeIcon/> : <DarkModeIcon/>}
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Profile Avatar */}
                    <Box sx={{ml: 1}}>
                        <Tooltip title="Account settings">
                            <IconButton
                                onClick={handleProfileClick}
                                size="small"
                                sx={{
                                    p: 0.5,
                                    border: '2px solid',
                                    borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)'
                                }}
                            >
                                {loading ? (
                                    <Skeleton variant="circular" width={40} height={40}/>
                                ) : (
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: '#3B82F6',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {getInitials(name)}
                                    </Avatar>
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Profile Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    TransitionComponent={Fade}
                    transformOrigin={{horizontal: "right", vertical: "top"}}
                    anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            width: 220,
                            borderRadius: '12px',
                            overflow: 'visible',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            border: '1px solid',
                            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                            bgcolor: darkMode ? '#1E293B' : 'white',
                            color: darkMode ? 'white' : 'inherit',
                            '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: -6,
                                right: 14,
                                width: 12,
                                height: 12,
                                bgcolor: darkMode ? '#1E293B' : 'white',
                                transform: 'rotate(45deg)',
                                zIndex: 0,
                                borderTop: '1px solid',
                                borderLeft: '1px solid',
                                borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                            }
                        }
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            textAlign: 'center',
                            borderBottom: '1px solid',
                            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 56,
                                height: 56,
                                margin: '0 auto 8px',
                                bgcolor: '#3B82F6'
                            }}
                        >
                            {getInitials(name)}
                        </Avatar>
                        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                            {name}
                        </Typography>
                        {(propertyName || unitNumber) && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {propertyName}{unitNumber ? ` • Unit ${unitNumber}` : ''}
                            </Typography>
                        )}
                    </Box>

                    <MenuItem
                        sx={{
                            py: 1.5,
                            color: darkMode ? 'white' : 'inherit'
                        }}
                    >
                        <ListItemIcon>
                            <PersonOutline fontSize="small" sx={{color: darkMode ? '#3B82F6' : '#3B82F6'}}/>
                        </ListItemIcon>
                        My Profile
                    </MenuItem>

                    <MenuItem
                        sx={{
                            py: 1.5,
                            color: darkMode ? 'white' : 'inherit'
                        }}
                    >
                        <ListItemIcon>
                            <DashboardIcon fontSize="small" sx={{color: darkMode ? '#8B5CF6' : '#8B5CF6'}}/>
                        </ListItemIcon>
                        Dashboard
                    </MenuItem>

                    {isMobile && (
                        <MenuItem
                            onClick={handleDarkModeToggle}
                            sx={{
                                py: 1.5,
                                color: darkMode ? 'white' : 'inherit'
                            }}
                        >
                            <ListItemIcon>
                                {darkMode ? (
                                    <LightModeIcon fontSize="small" sx={{color: '#10B981'}}/>
                                ) : (
                                    <DarkModeIcon fontSize="small" sx={{color: '#6B7280'}}/>
                                )}
                            </ListItemIcon>
                            {darkMode ? 'Light Mode' : 'Dark Mode'}
                            <Switch
                                size="small"
                                checked={darkMode}
                                sx={{ml: 'auto'}}
                            />
                        </MenuItem>
                    )}

                    <Divider sx={{my: 1, borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'inherit'}}/>

                    <Link to="/" style={{textDecoration: 'none', color: 'inherit'}}>
                        <MenuItem
                            onClick={logout}
                            sx={{
                                py: 1.5,
                                color: '#EF4444'
                            }}
                        >
                            <ListItemIcon>
                                <Logout fontSize="small" sx={{color: '#EF4444'}}/>
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Link>
                </Menu>

                {/* Notifications Menu */}
                <Menu
                    anchorEl={notificationsAnchorEl}
                    open={notificationsOpen}
                    onClose={handleNotificationsClose}
                    TransitionComponent={Fade}
                    transformOrigin={{horizontal: "right", vertical: "top"}}
                    anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            maxWidth: 360,
                            width: {xs: 'calc(100vw - 32px)', sm: 360},
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            border: '1px solid',
                            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                            bgcolor: darkMode ? '#1E293B' : 'white',
                            color: darkMode ? 'white' : 'inherit',
                            '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: -6,
                                right: 14,
                                width: 12,
                                height: 12,
                                bgcolor: darkMode ? '#1E293B' : 'white',
                                transform: 'rotate(45deg)',
                                zIndex: 0,
                                borderTop: '1px solid',
                                borderLeft: '1px solid',
                                borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                            }
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderBottom: '1px solid',
                            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                        }}
                    >
                        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                            Notifications
                        </Typography>
                        <Button
                            size="small"
                            sx={{
                                color: '#3B82F6',
                                textTransform: 'none',
                                fontSize: '0.8rem'
                            }}
                            onClick={markAllAsRead}
                        >
                            Mark all as read
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            p: 1.5,
                            borderTop: '1px solid',
                            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                            textAlign: 'center'
                        }}
                    >
                        <Button
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                color: '#3B82F6'
                            }}
                        >
                            View all notifications
                        </Button>
                    </Box>
                </Menu>
            </Box>
        </Paper>
    );
}